import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';

import { AttendanceDashboardPeriodFilter } from 'components/molecules/AttendanceDashboardPeriodFilter';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import CircularProgress from '@mui/material/CircularProgress';
import { Typography } from 'components';
import React, { useEffect, useMemo, useRef } from 'react';
import {
  AttendanceDashboardRow,
  AttendanceDashboardSummary
} from 'services/AttendanceTrackingService';
import {
  ATTENDANCE_STATUS_COLUMN_LABELS,
  buildAttendanceStatusLegend,
  DEFAULT_FULL_DAY_HOURS,
  DEFAULT_SHORT_DAY_HOURS,
  formatDashboardLoadedCount,
  getAttendanceStatusClass
} from 'utils/formatAttendanceDashboard';
import {
  AttendanceDashboardPeriod,
  isFullMonthDashboardPeriod
} from 'utils/attendanceDashboardPeriod';
import './index.scss';

interface KpiItem {
  key: string;
  label: string;
  value: number;
  variant: 'new' | 'total' | 'dup' | 'warn' | 'leave';
}
interface Props {
  summary: AttendanceDashboardSummary | null;
  items: AttendanceDashboardRow[];
  totalCount: number;
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  search: string;
  onSearchChange: (value: string) => void;
  period: AttendanceDashboardPeriod;
  onPeriodChange: (period: AttendanceDashboardPeriod) => void;
}

const formatCell = (value?: string) => value ?? '—';

export const AttendanceDashboardPanel = ({
  summary,
  items,
  totalCount,
  loading,
  loadingMore,
  hasMore,
  onLoadMore,
  search,
  onSearchChange,
  period,
  onPeriodChange
}: Props) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLTableRowElement>(null);

  const fullDayHours = summary?.fullDayHoursThreshold ?? DEFAULT_FULL_DAY_HOURS;
  const shortDayHours =
    summary?.shortDayHoursThreshold ?? DEFAULT_SHORT_DAY_HOURS;

  const statusLegend = useMemo(
    () => buildAttendanceStatusLegend(fullDayHours, shortDayHours),
    [fullDayHours, shortDayHours]
  );

  const renderStatus = (row: AttendanceDashboardRow) => (
    <span
      className={`attendance-dashboard__status-text attendance-dashboard__status-text--${getAttendanceStatusClass(
        row.status
      )}`}
    >
      {ATTENDANCE_STATUS_COLUMN_LABELS[row.status]}
    </span>
  );

  const showBelowCompliance = isFullMonthDashboardPeriod(period);
  const belowComplianceLabel =
    showBelowCompliance && summary
      ? `Below Compliance (<${summary.minimumOfficeDaysPerMonth} office days)`
      : 'Below Compliance';

  const kpis: KpiItem[] = summary
    ? [
        {
          key: 'total',
          label: 'Total Employees',
          value: summary.totalEmployees,
          variant: 'total'
        },
        ...(showBelowCompliance
          ? [
              {
                key: 'compliance',
                label: belowComplianceLabel,
                value: summary.belowCompliance ?? 0,
                variant: 'warn' as const
              }
            ]
          : []),
        {
          key: 'missing',
          label: 'Missing Attendance',
          value: summary.missingAttendance,
          variant: 'dup'
        },
        {
          key: 'leave',
          label: 'On Leave',
          value: summary.onLeave,
          variant: 'leave'
        }
      ]
    : [];

  useEffect(() => {
    const root = scrollRef.current;
    const target = loadMoreRef.current;

    if (!root || !target || !hasMore || loading || loadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          onLoadMore();
        }
      },
      {
        root,
        rootMargin: '120px',
        threshold: 0
      }
    );

    observer.observe(target);

    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, items.length, onLoadMore]);

  useEffect(() => {
    const root = scrollRef.current;
    if (!root || loading || loadingMore || !hasMore) return;

    if (root.scrollHeight <= root.clientHeight + 4) {
      onLoadMore();
    }
  }, [hasMore, items.length, loading, loadingMore, onLoadMore]);

  return (
    <div className="attendance-dashboard">
      <div
        className={`attendance-dashboard__metrics${
          !showBelowCompliance ? ' attendance-dashboard__metrics--three' : ''
        }`}
      >
        {kpis.map((kpi) => (
          <article
            key={kpi.key}
            className={`attendance-dashboard__metric attendance-dashboard__metric--${kpi.variant}`}
          >
            <div className="attendance-dashboard__metric-body">
              <Typography
                type="xs_medium"
                text={kpi.label}
                weight="bold"
                className="attendance-dashboard__metric-label"
              />
              <Typography
                type="heading4"
                text={loading ? '…' : String(kpi.value)}
                weight="bold"
                className="attendance-dashboard__metric-value"
              />
            </div>
          </article>
        ))}
      </div>

      <section className="attendance-dashboard__card">
        <div className="attendance-dashboard__card-header">
          <span className="attendance-dashboard__card-icon" aria-hidden>
            <TableChartOutlinedIcon fontSize="small" />
          </span>
          <div>
            <Typography type="heading4" text="Attendance" weight="bold" />
            <Typography
              type="small_regular"
              text="Daily punch records for the selected period."
              className="attendance-dashboard__card-hint"
            />
          </div>
        </div>

        <div className="attendance-dashboard__toolbar">
          <label className="attendance-dashboard__search">
            <SearchOutlinedIcon fontSize="small" aria-hidden />
            <input
              type="search"
              value={search}
              placeholder="Search name or ID..."
              onChange={(event) => onSearchChange(event.target.value)}
            />
          </label>

          <div className="attendance-dashboard__toolbar-period">
            <AttendanceDashboardPeriodFilter
              period={period}
              onChange={onPeriodChange}
            />
          </div>
        </div>

        <div
          className="attendance-dashboard__legend"
          aria-label="Attendance status legend"
        >
          {statusLegend.map((item) => (
            <span
              key={item.status}
              className={`attendance-dashboard__legend-item attendance-dashboard__legend-item--${getAttendanceStatusClass(
                item.status
              )}`}
            >
              {item.label}
            </span>
          ))}
        </div>

        <div
          className="attendance-dashboard__table-wrap"
          ref={scrollRef}
          aria-busy={loadingMore}
        >
          {loading ? (
            <div className="attendance-dashboard__table-loading">
              <CircularProgress size={28} />
              <span>Loading attendance records...</span>
            </div>
          ) : items.length > 0 ? (
            <table className="attendance-dashboard__table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Employee ID</th>
                  <th>Name</th>
                  <th>Arrival</th>
                  <th>Departure</th>
                  <th>Hours</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row, index) => (
                  <tr key={`${row.date}-${row.employeeId}-${index}`}>
                    <td>{row.dayLabel}</td>
                    <td>{row.employeeId}</td>
                    <td>{row.employeeName}</td>
                    <td>{formatCell(row.arrival)}</td>
                    <td>{formatCell(row.departure)}</td>
                    <td>{formatCell(row.hoursWorked)}</td>
                    <td>{renderStatus(row)}</td>
                  </tr>
                ))}
                {hasMore && (
                  <tr
                    ref={loadMoreRef}
                    className="attendance-dashboard__load-more-row"
                  >
                    <td colSpan={7}>
                      <span className="attendance-dashboard__load-more">
                        {loadingMore ? (
                          <>
                            <CircularProgress size={18} />
                            Loading more records...
                          </>
                        ) : (
                          'Scroll for more'
                        )}
                      </span>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          ) : (
            <div className="attendance-dashboard__empty">
              No attendance records match your search.
            </div>
          )}
        </div>

        <footer className="attendance-dashboard__footer">
          <span className="attendance-dashboard__footer-meta">
            {formatDashboardLoadedCount(items.length, totalCount)}
          </span>
        </footer>
      </section>
    </div>
  );
};
